import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/news";
import { hotArticles, searchArticles } from "@/utils/news";

const SORT_OPTIONS: { key: "new" | "hot"; label: string }[] = [
  { key: "new", label: "最新发布" },
  { key: "hot", label: "最多热度" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState<"new" | "hot">("new");

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const results = useMemo(() => {
    let list = searchArticles(q);
    if (cat !== "all") list = list.filter((a) => a.category === cat);
    list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return a.date < b.date ? 1 : -1;
    });
    return list;
  }, [q, cat, sort]);

  const recommend = hotArticles(4);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setParams({ q: keyword.trim() });
  };

  return (
    <div className="bg-background-50">
      <Seo
        title={q ? `“${q}” 的搜索结果 - 51爆料` : "站内搜索 - 51爆料"}
        description="使用 51爆料站内搜索，快速查找社会、娱乐、科技等领域的爆料与热点新闻。"
        keywords="51爆料搜索,热点搜索,爆料查询"
      />

      <div className="bg-secondary-500 text-white">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-heading font-black">
            站内搜索
          </h1>
          <p className="mt-2 text-sm md:text-base text-white/85">
            输入关键词，快速定位你想看的爆料内容。
          </p>
          <form
            onSubmit={submit}
            className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl"
          >
            <div className="flex items-center flex-1 bg-background-50 rounded-md px-3 h-11">
              <i className="ri-search-line text-foreground-400 text-lg" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例如：夜市、税务、AI"
                className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-8">
        {q ? (
          <>
            <p className="text-sm text-foreground-500 mb-5">
              关键词 “<span className="text-primary-600 font-medium">{q}</span>”
              共匹配到{" "}
              <span className="text-primary-600 font-bold">{results.length}</span>{" "}
              条结果
            </p>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">类别</span>
                <button
                  type="button"
                  onClick={() => setCat("all")}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    cat === "all"
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  全部
                </button>
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCat(c.slug)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      cat === c.slug
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                <span className="text-sm text-foreground-500 mr-1">排序</span>
                {SORT_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setSort(o.key)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === o.key
                        ? "bg-accent-500 text-foreground-950"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="flex flex-col gap-4">
                {results.map((a) => (
                  <ArticleCard key={a.id} article={a} layout="row" />
                ))}
              </div>
            ) : (
              <div>
                <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-14 px-6 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                    <i className="ri-search-eye-line" />
                  </div>
                  <p className="mt-4 text-base font-medium text-foreground-800">
                    没有找到与 “{q}” 相关的爆料
                  </p>
                  <p className="mt-1 text-sm text-foreground-500">
                    换个关键词试试，或看看下面的热门推荐。
                  </p>
                </div>

                <h2 className="text-lg font-heading font-black text-foreground-950 mt-10 mb-4">
                  热门推荐
                </h2>
                <div className="flex flex-col gap-4">
                  {recommend.map((a) => (
                    <ArticleCard key={`rec-${a.id}`} article={a} layout="row" />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="bg-background-100 border border-background-200 rounded-lg py-14 px-6 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-50 text-3xl text-foreground-400">
                <i className="ri-search-line" />
              </div>
              <p className="mt-4 text-base font-medium text-foreground-800">
                输入关键词，开始搜索
              </p>
              <p className="mt-1 text-sm text-foreground-500">
                支持按标题、摘要与标签进行匹配。
              </p>
            </div>

            <h2 className="text-lg font-heading font-black text-foreground-950 mt-10 mb-4">
              大家都在看
            </h2>
            <div className="flex flex-col gap-4">
              {recommend.map((a) => (
                <ArticleCard key={`hot-${a.id}`} article={a} layout="row" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}